"use client";

import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import HeroSection from "./components/PinecrestHero";
import NeighborhoodStats from "@/components/feature/NeighborhoodStats";
import RepairServicesGrid from "@/components/feature/RepairServicesGrid";
import ScopeDisclaimer from "@/components/feature/ScopeDisclaimer";
import NeighborhoodCta from "@/components/feature/NeighborhoodCta";
import QuartierCrossLinks from "@/components/feature/QuartierCrossLinks";
import { injectMetaTags } from "@/lib/seoHelpers";
import FaqSection from "@/components/feature/FaqSection";
import RelatedServicesBlock from "@/components/feature/RelatedServicesBlock";
import NearbyAreasBlock from "@/components/feature/NearbyAreasBlock";
import AboutAreaSection from "@/components/feature/AboutAreaSection";

const neighborhood = "Pinecrest";

export default function PinecrestPage() {
  const { t, currentLang } = useLanguage();
  useHreflang();

  const pinecrestFaqs = [
    { question: t("hood.faq1Q"), answer: t("hood.faq1A") },
    { question: t("hood.faq2Q"), answer: t("hood.faq2A") },
    { question: t("hood.faq3Q"), answer: t("hood.faq3A") },
    { question: t("hood.faq4Q"), answer: t("hood.faq4A") },
    { question: t("pinecrest.faq5Q"), answer: t("pinecrest.faq5A") },
  ];

  useEffect(() => {
    document.title =
      currentLang === "es"
        ? "Reparaci\u00F3n de Ventanas Pinecrest \u2014 Casas Estate Miami"
        : "Pinecrest Window Repair \u2014 Estate Home Specialists";

    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.content =
      currentLang === "es"
        ? "Reparaci\u00F3n de ventanas en casas estate de Pinecrest. Mantenimiento integral para propiedades grandes con 20+ ventanas. B\u00FAsqueda de herrajes premium y servicio multi-visita Miami-Dade."
        : "Pinecrest estate home window repair. Comprehensive maintenance for large properties with 20+ windows. Premium hardware sourcing and multi-visit service Miami-Dade.";

    const cleanupSeo = injectMetaTags({
      title: document.title,
      description: metaDesc.content,
      url: "https://impact-window-repair-miami.com/impact-windows-pinecrest/",
      image: "https://impact-window-repair-miami.com/og/og-pinecrest.jpg",
      imageAlt:
        currentLang === "es"
          ? "Reparaci\u00F3n de ventanas Pinecrest Miami"
          : "Window repair Pinecrest Miami",
      breadcrumbs: [
        { name: currentLang === "es" ? "Inicio" : "Home", item: "https://impact-window-repair-miami.com/" },
        { name: currentLang === "es" ? "\u00C1reas de Servicio" : "Service Areas", item: "https://impact-window-repair-miami.com/service-areas/" },
        { name: neighborhood, item: "https://impact-window-repair-miami.com/impact-windows-pinecrest/" },
      ],
      type: "website",
    });

    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: "Window Repair",
          provider: {
            "@type": "LocalBusiness",
            name: "Windows and Doors Repair LLC",
            telephone: "+17863774452",
            email: "contact@impact-window-repair-miami.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: "848 Brickell Ave, PH-5",
              addressLocality: "Miami",
              addressRegion: "FL",
              postalCode: "33131",
              addressCountry: "US",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: 25.7617,
              longitude: -80.1918,
            },
            openingHoursSpecification: [{
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              opens: "07:00",
              closes: "21:00",
            }],
            priceRange: "$$",
            paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
            currenciesAccepted: "USD",
            areaServed: [
              { "@type": "City", name: "Miami" },
              { "@type": "AdministrativeArea", name: "Miami-Dade County" },
              "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach",
            ],
            knowsLanguage: ["en", "es"],
            knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
            slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          },
          areaServed: {
            "@type": "City",
            name: "Pinecrest, Miami-Dade, FL",
          },
          serviceType: "Window Repair",
          description:
            currentLang === "es"
              ? "Reparaci\u00F3n de herrajes, vidrios y mantenimiento en Pinecrest."
              : "Hardware repair, glass replacement, and maintenance in Pinecrest.",
        },
        {
          "@type": "LocalBusiness",
          name: "Windows and Doors Repair LLC",
          telephone: "+17863774452",
          email: "contact@impact-window-repair-miami.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "848 Brickell Ave, PH-5",
            addressLocality: "Miami",
            addressRegion: "FL",
            postalCode: "33131",
            addressCountry: "US",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 25.7617,
            longitude: -80.1918,
          },
          openingHoursSpecification: [{
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "07:00",
            closes: "21:00",
          }],
          priceRange: "$$",
          paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
          currenciesAccepted: "USD",
          areaServed: [
            { "@type": "City", name: "Miami" },
            { "@type": "AdministrativeArea", name: "Miami-Dade County" },
            "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach",
          ],
          knowsLanguage: ["en", "es"],
          knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
          slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          url: window.location.href,
        },
      ],
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: pinecrestFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.id = "faq-schema";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      cleanupSeo();
      document.head.removeChild(script);
      const existingFaq = document.getElementById("faq-schema");
      if (existingFaq) document.head.removeChild(existingFaq);
    };
  }, [currentLang]);

  return (
    <main className="min-h-screen">
      <HeroSection
        h1={t("pinecrest.h1")}
        subhead={t("pinecrest.subhead")}
        zips={["33156", "33158"]}
      />
      <AboutAreaSection
        badge={currentLang === "es" ? "REPARACI\u00D3N EN PINECREST" : "PINECREST WINDOW REPAIR"}
        imageSrc="https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/1d3db4f3-b9fa-4138-8319-4fd27a32cf7d_6253511076dab.webp?v=de1cdb3602c68b21a6353651ce39d68b"
        imageAlt={currentLang === "es" ? "Especialista en reparaci\u00F3n de ventanas Pinecrest Miami" : "Pinecrest Miami window repair specialist servicing estate-scale residential property"}
        heading={currentLang === "es" ? "Reparaci\u00F3n de Ventanas para Estates en Pinecrest" : "Pinecrest Estate Home Window Repair"}
        lead={t("pinecrest.intro")}
        bullets={
          currentLang === "es"
            ? [
                "Reparaci\u00F3n integral multi-visita para casas grandes",
                "B\u00FAsqueda y reemplazo de herrajes premium",
                "Informes escritos identificando prioridades futuras",
              ]
            : [
                "Multi-visit comprehensive repair for large homes",
                "Premium hardware sourcing and replacement",
                "Written reports identifying future repair priorities",
              ]
        }
        closing={
          currentLang === "es"
            ? "Las casas estate de Pinecrest se benefician de nuestros planes de mantenimiento integral adaptados al alcance de propiedades grandes."
            : "Pinecrest estate homes benefit from our comprehensive maintenance plans tailored to large property scope."
        }
      />
      <NeighborhoodStats />
      <RepairServicesGrid heading={t("hood.commonH2")} />
      <ScopeDisclaimer neighborhood={neighborhood} />
      <FaqSection heading={t("hood.commonFaqH2")} items={pinecrestFaqs} />
      <RelatedServicesBlock neighborhood={neighborhood} />
      <NearbyAreasBlock neighborhood={neighborhood} neighbors={[
        { name: currentLang === "es" ? "Kendall" : "Kendall", slug: "kendall" },
        { name: currentLang === "es" ? "South Miami" : "South Miami", slug: "south-miami" },
        { name: currentLang === "es" ? "Coral Gables" : "Coral Gables", slug: "coral-gables" },
      ]} />
      <NeighborhoodCta neighborhood={neighborhood} heading={t("hood.commonCta")} />
      <QuartierCrossLinks />
    </main>
  );
}